import { useState, useEffect, useCallback } from 'react';
import type { LucideIcon } from 'lucide-react';
import { HelpCircle, Info ,
  Phone,
  MessageSquare,
  Settings,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Eye,
  EyeOff,
  ExternalLink,
  Zap,
  AlertTriangle,
  Server,
  Users,
  Chrome,
  Unlink,
  Plus,
  Trash2,
  Shield,
  Flame,
  Globe2,
  ImageIcon,
  Target,
  Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { apiClient, ApiResponse } from '@/lib/api';
import { getErrorMessage } from '../../lib/utils';
import type {
  AcubeAccountConfig, AcubeBusinessRegistrySyncResult, AcubeCompanyBrcStatus, AcubeCredentialsCheck,
  AcubeEnvironmentPolicy, AcubeIntegrationState, AcubeWebhookDiagnostics, AcubeWebhookPublicKeyEntry,
} from '@easyflow/shared';
import GoogleIntegrationConfig from './GoogleIntegrationConfig';
import MetaBusinessCard from './MetaBusinessCard';


interface IntegrationTemplate {
  type: string;
  name: string;
  description: string;
  icon?: string;
  configured: boolean;
  enabled: boolean;
  status: 'not_configured' | 'configured' | 'active' | 'error';
  health_status: 'healthy' | 'unhealthy' | 'unknown';
  has_credentials: boolean;
  integration_id?: string;
  mcp_server_id?: string;
  required_credentials: string[];
  optional_credentials: string[];
  // Campi arricchiti presenti solo su alcuni template (es. A-Cube).
  default_config?: AcubeConfig;
  environment_policy?: AcubePolicy | null;
}

interface ConfigureDialogState {
  open: boolean;
  template: IntegrationTemplate | null;
}

type AcubeConfig = AcubeAccountConfig;
type AcubePolicy = AcubeEnvironmentPolicy;

function AcubePublicKeyLine({ label, entry }: { label: string; entry: AcubeWebhookPublicKeyEntry | null }) {
  if (!entry) return <p className="text-muted-foreground">{label}: non in cache</p>;
  return (
    <p>
      {label}: <code>{entry.fingerprint}</code>
      <span className="text-muted-foreground">
        {' '}· aggiornata {new Date(entry.fetched_at).toLocaleString('it-IT')} · valida fino a {new Date(entry.expires_at).toLocaleString('it-IT')}
      </span>
    </p>
  );
}

interface GoogleAccountSummary {
  id: string;
  authorized?: boolean;
  enabled?: boolean;
  app_published?: boolean;
}

export default function IntegrationTemplatesTab() {
  const [integrations, setIntegrations] = useState<IntegrationTemplate[]>([]);
  const [googleAccounts, setGoogleAccounts] = useState<GoogleAccountSummary[]>([]);
  const [googleStatusError, setGoogleStatusError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [configDialog, setConfigDialog] = useState<ConfigureDialogState>({ open: false, template: null });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  const [credentials, setCredentials] = useState<Record<string, string>>({});

  // A-Cube: config account (DTO condiviso), politica ambiente, BRC per RS ed esito ultima verifica credenziali.
  const [acubeConfig, setAcubeConfig] = useState<AcubeConfig>({});
  const [acubePolicy, setAcubePolicy] = useState<AcubePolicy | null>(null);
  const [acubeCompanies, setAcubeCompanies] = useState<AcubeCompanyBrcStatus[]>([]);
  const [acubeWebhook, setAcubeWebhook] = useState<AcubeWebhookDiagnostics | null>(null);
  const [acubeCheck, setAcubeCheck] = useState<AcubeCredentialsCheck | null>(null);
  const [acubeBusy, setAcubeBusy] = useState(false);

  // Fatture in Cloud specialized state — OAuth + azienda + webhook
  interface FicCompanyView { id: number; name: string; type?: string | null }
  const [ficCompanies, setFicCompanies] = useState<FicCompanyView[]>([]);
  const [ficSelectedCompany, setFicSelectedCompany] = useState<number | null>(null);
  const [ficConnected, setFicConnected] = useState<boolean | null>(null);
  const [ficBusy, setFicBusy] = useState(false);

  const loadFicCompanies = useCallback(async () => {
    try {
      const r = await apiClient.get<{ companies?: FicCompanyView[]; selected_company_id?: number | null }>(
        '/integrations/fic/companies',
      );
      if (r.success && r.data) {
        setFicConnected(true);
        setFicCompanies(r.data.companies ?? []);
        setFicSelectedCompany(r.data.selected_company_id ?? null);
      } else {
        setFicConnected(false);
        setFicCompanies([]);
      }
    } catch {
      setFicConnected(false);
      setFicCompanies([]);
    }
  }, []);

  // Listener esito popup OAuth FIC (postMessage source 'fic-oauth')
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { source?: string; status?: string; error?: string | null } | null;
      if (!data || data.source !== 'fic-oauth') return;
      if (data.status === 'success') {
        toast({ title: 'Fatture in Cloud collegato', description: 'Autorizzazione OAuth completata.' });
        void loadFicCompanies();
      } else {
        toast({ title: 'Errore OAuth Fatture in Cloud', description: data.error || 'Autorizzazione negata', variant: 'destructive' });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [loadFicCompanies, toast]);

  const handleFicConnect = async () => {
    setFicBusy(true);
    try {
      const r = await apiClient.get<{ authUrl: string }>('/integrations/fic/oauth/start');
      if (r.success && r.data?.authUrl) {
        window.open(r.data.authUrl, 'fic-oauth', 'width=560,height=720');
      } else {
        toast({ title: 'Errore', description: r.error || 'Impossibile avviare OAuth', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore', description: e instanceof Error ? e.message : 'Impossibile avviare OAuth', variant: 'destructive' });
    } finally { setFicBusy(false); }
  };

  const handleFicSelectCompany = async (companyId: number) => {
    setFicBusy(true);
    try {
      const r = await apiClient.post('/integrations/fic/company', { company_id: companyId });
      if (r.success) {
        setFicSelectedCompany(companyId);
        toast({ title: 'Azienda selezionata', description: `Azienda Fatture in Cloud #${companyId} attiva.` });
      } else {
        toast({ title: 'Errore', description: r.error || 'Selezione azienda fallita', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore', description: e instanceof Error ? e.message : 'Selezione azienda fallita', variant: 'destructive' });
    } finally { setFicBusy(false); }
  };

  const handleFicSubscribeWebhook = async () => {
    setFicBusy(true);
    try {
      const r = await apiClient.post<{ subscription_id: string | null }>('/integrations/fic/webhook/subscribe', {});
      if (r.success) {
        toast({ title: 'Webhook attivo', description: 'Aggiornamenti di stato SdI in tempo reale abilitati.' });
      } else {
        toast({ title: 'Errore webhook', description: r.error || 'Attivazione webhook fallita', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore webhook', description: e instanceof Error ? e.message : 'Attivazione webhook fallita', variant: 'destructive' });
    } finally { setFicBusy(false); }
  };

  // Telegram specialized state — bot CRUD via /integrations/telegram/bots
  interface TelegramBotView {
    id: string;
    bot_external_id: string;
    username: string | null;
    display_name: string;
    friendly_name: string | null;
    purpose: 'notifications' | 'messaging' | 'both';
    status: 'active' | 'disabled' | 'error' | string;
    webhook_url: string | null;
    last_error: string | null;
    created_at: string;
  }
  const [telegramBots, setTelegramBots] = useState<TelegramBotView[]>([]);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramForm, setTelegramForm] = useState<{
    botToken: string;
    friendlyName: string;
    purpose: 'notifications' | 'messaging' | 'both';
  }>({ botToken: '', friendlyName: '', purpose: 'both' });
  const [telegramAdding, setTelegramAdding] = useState(false);
  const [telegramDeleting, setTelegramDeleting] = useState<string | null>(null);
  const [telegramTesting, setTelegramTesting] = useState<string | null>(null);

  const loadTelegramBots = async () => {
    setTelegramLoading(true);
    try {
      const r = await apiClient.get<{ bots: TelegramBotView[] }>('/integrations/telegram/bots');
      if (r.success && r.data) setTelegramBots(r.data.bots || []);
    } catch { /* ignore */ }
    finally { setTelegramLoading(false); }
  };

  const handleAddTelegramBot = async () => {
    const token = telegramForm.botToken.trim();
    if (!token) {
      toast({ title: 'Token mancante', description: 'Incolla il token del bot ottenuto da @BotFather', variant: 'destructive' });
      return;
    }
    setTelegramAdding(true);
    try {
      const r = await apiClient.post<{ bot: TelegramBotView; warning: string | null }>(
        '/integrations/telegram/bots',
        {
          botToken: token,
          friendlyName: telegramForm.friendlyName.trim() || undefined,
          purpose: telegramForm.purpose,
        },
      );
      if (r.success && r.data?.bot) {
        toast({
          title: 'Bot aggiunto',
          description: r.data.warning
            ? `Bot @${r.data.bot.username ?? r.data.bot.bot_external_id} registrato (avviso: ${r.data.warning})`
            : `Bot @${r.data.bot.username ?? r.data.bot.bot_external_id} attivo`,
        });
        setTelegramForm({ botToken: '', friendlyName: '', purpose: 'both' });
        await loadTelegramBots();
        await loadIntegrations();
      } else {
        toast({ title: 'Errore', description: r.error || 'Registrazione fallita', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore', description: (e as Error).message, variant: 'destructive' });
    } finally { setTelegramAdding(false); }
  };

  const handleDeleteTelegramBot = async (bot: TelegramBotView) => {
    if (!confirm(`Rimuovere il bot @${bot.username ?? bot.bot_external_id}? Il webhook Telegram verrà disattivato.`)) return;
    setTelegramDeleting(bot.id);
    try {
      const r = await apiClient.delete<{ deleted: boolean }>(`/integrations/telegram/bots/${bot.id}`);
      if (r.success) {
        toast({ title: 'Bot rimosso', description: `@${bot.username ?? bot.bot_external_id} disattivato` });
        await loadTelegramBots();
        await loadIntegrations();
      } else {
        toast({ title: 'Errore', description: r.error || 'Rimozione fallita', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore', description: (e as Error).message, variant: 'destructive' });
    } finally { setTelegramDeleting(null); }
  };

  const handleTestTelegramBot = async (bot: TelegramBotView) => {
    setTelegramTesting(bot.id);
    try {
      const r = await apiClient.post<{ ok: boolean; info?: { username?: string; first_name?: string } }>(
        `/integrations/telegram/bots/${bot.id}/test`, {});
      if (r.success && r.data?.ok) {
        toast({
          title: 'Bot raggiungibile',
          description: `getMe ok — @${r.data.info?.username ?? bot.username ?? bot.bot_external_id}`,
        });
        await loadTelegramBots();
      } else {
        toast({ title: 'Test fallito', description: r.error || 'Telegram non risponde', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore', description: (e as Error).message, variant: 'destructive' });
    } finally { setTelegramTesting(null); }
  };

  // Effective environment for the A-Cube form: user's explicit pick overrides
  // the policy default. Sandbox = test playground → form hides legal-rep and
  // other production-only sections (mirrored by the backend
  // validateAcubeConfig sandbox shortcut).
  const acubeEnv: 'sandbox' | 'production' =
    acubeConfig.environment
    || acubePolicy?.active_environment
    || acubePolicy?.default_environment
    || 'production';
  const isAcubeSandbox = acubeEnv === 'sandbox';

  // Inline help-tooltip used next to labels and service toggles. Wrapped in a
  // single TooltipProvider further down so we don't pay the provider cost for
  // the rest of the integrations list.
  const Hint = ({ text, icon: IconComp = HelpCircle }: { text: React.ReactNode; icon?: LucideIcon }) => (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <button type="button" className="inline-flex text-muted-foreground hover:text-foreground" aria-label="Aiuto">
          <IconComp className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs whitespace-normal text-xs leading-relaxed">
        {text}
      </TooltipContent>
    </Tooltip>
  );

  const loadAcubeState = async () => {
    try {
      const r = await apiClient.get<AcubeIntegrationState>('/integrations/acube/state');
      if (r.success && r.data) {
        const remote = r.data.config;
        setAcubeConfig((prev) => (Object.keys(remote).length > 0 ? { ...prev, ...remote } : prev));
        setAcubePolicy(r.data.environment_policy);
        setAcubeCompanies(r.data.companies);
        setAcubeWebhook(r.data.webhook);
      }
    } catch (e) {
      toast({ title: 'Stato A-Cube non disponibile', description: getErrorMessage(e), variant: 'destructive' });
    }
  };

  const handleAcubeVerifyCredentials = async () => {
    setAcubeBusy(true);
    try {
      const r = await apiClient.post<AcubeCredentialsCheck>('/integrations/acube/verify-credentials', {});
      if (r.success && r.data) {
        setAcubeCheck(r.data);
        toast({
          title: r.data.ok ? 'Credenziali A-Cube valide' : 'Credenziali A-Cube non valide',
          description: r.data.token_expires_at
            ? `${r.data.message} — token valido fino a ${new Date(r.data.token_expires_at).toLocaleString('it-IT')}`
            : r.data.message,
          variant: r.data.ok ? undefined : 'destructive',
        });
      } else {
        toast({ title: 'Verifica fallita', description: r.error || 'Login A-Cube non riuscito', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Verifica fallita', description: getErrorMessage(e), variant: 'destructive' });
    } finally { setAcubeBusy(false); }
  };

  const handleAcubeSyncBusinessRegistry = async () => {
    setAcubeBusy(true);
    try {
      const r = await apiClient.post<AcubeBusinessRegistrySyncResult>('/integrations/acube/sync-business-registry', {});
      if (r.success && r.data) {
        const failed = r.data.entries.filter((e) => e.error !== null);
        setAcubeCompanies(r.data.companies);
        toast({
          title: 'Business registry sincronizzato',
          description: failed.length > 0
            ? `${r.data.entries.length - failed.length}/${r.data.entries.length} ragioni sociali allineate (${r.data.environment}); errori: ${failed.map((f) => f.error).join('; ')}`
            : `${r.data.entries.length} ragioni sociali allineate su A-Cube (${r.data.environment})`,
          variant: failed.length > 0 ? 'destructive' : undefined,
        });
        await loadAcubeState();
      } else {
        toast({ title: 'Errore', description: r.error || 'Sync fallita', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' });
    } finally { setAcubeBusy(false); }
  };

  const loadIntegrations = useCallback(async () => {
    try {
      setLoading(true);
      const [response, googleStatus] = await Promise.all([
        apiClient.get<IntegrationTemplate[]>('/integrations'),
        apiClient.get<{ accounts?: GoogleAccountSummary[] }>('/integrations/google/oauth/status').catch(() => null)
      ]);
      if (response.success && response.data) {
        setIntegrations(response.data);
      }
      if (googleStatus?.success && googleStatus.data) {
        setGoogleAccounts(googleStatus.data.accounts ?? []);
        setGoogleStatusError(false);
      } else {
        setGoogleAccounts([]);
        setGoogleStatusError(true);
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile caricare le integrazioni',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  const openConfigDialog = (template: IntegrationTemplate) => {
    setCredentials({});
    setConfigDialog({ open: true, template });
    if (template.type === 'acube') {
      // Seed config with defaults from the template definition, then merge saved state
      setAcubeConfig({ ...(template.default_config || {}) });
      setAcubePolicy(template.environment_policy || null);
      setAcubeCompanies([]);
      setAcubeCheck(null);
      loadAcubeState();
    }
    if (template.type === 'telegram') {
      setTelegramBots([]);
      setTelegramForm({ botToken: '', friendlyName: '', purpose: 'both' });
      loadTelegramBots();
    }
    if (template.type === 'fic') {
      setFicCompanies([]);
      setFicConnected(null);
      if (template.configured) void loadFicCompanies();
    }
  };

  const handleSaveCredentials = async () => {
    if (!configDialog.template) return;

    const required = configDialog.template.required_credentials || [];
    const hasAnyCredential = Object.values(credentials).some(v => v && v.trim());
    
    // If not already configured, all required fields must be filled
    // (A-Cube validates server-side per casistica; skip the generic check here)
    if (!configDialog.template.configured && configDialog.template.type !== 'acube') {
      for (const field of required) {
        if (!credentials[field]) {
          toast({
            title: 'Errore',
            description: `Il campo ${field} è obbligatorio`,
            variant: 'destructive'
          });
          return;
        }
      }
    } else if (hasAnyCredential && configDialog.template.type !== 'acube') {
      // If already configured and user entered some credentials, validate all required are filled.
      // A-Cube is exempt: backend merges partial credential updates with the existing
      // encrypted blob, so editing only one field (e.g. AdE password or legal-rep CF)
      // must not force the user to re-enter every required field.
      for (const field of required) {
        if (!credentials[field]) {
          toast({
            title: 'Errore',
            description: `Se modifichi le credenziali, inserisci tutti i campi obbligatori`,
            variant: 'destructive'
          });
          return;
        }
      }
    }

    // If already configured and no new credentials entered, just close the dialog.
    // Exception: A-Cube allows config-only updates (services, webhook events,
    // subject_type, etc.) without re-entering email/password — its backend
    // merges with existing encrypted credentials.
    if (configDialog.template.configured && !hasAnyCredential && configDialog.template.type !== 'acube') {
      setConfigDialog({ open: false, template: null });
      toast({
        title: 'Nessuna modifica',
        description: 'Le credenziali esistenti sono state mantenute'
      });
      return;
    }

    // Twilio-specific validation: require at least one auth method
    if (configDialog.template.type === 'twilio' && hasAnyCredential) {
      const hasAuthToken = !!credentials.auth_token?.trim();
      const hasApiKey = !!credentials.api_key_sid?.trim() && !!credentials.api_secret?.trim();
      
      if (!hasAuthToken && !hasApiKey) {
        toast({
          title: 'Metodo di autenticazione richiesto',
          description: 'Inserisci almeno Auth Token oppure API Key SID + API Secret',
          variant: 'destructive'
        });
        return;
      }
      
      // Check for incomplete API key pair
      if ((credentials.api_key_sid?.trim() && !credentials.api_secret?.trim()) || 
          (!credentials.api_key_sid?.trim() && credentials.api_secret?.trim())) {
        toast({
          title: 'Coppia API Key incompleta',
          description: 'API Key SID e API Secret devono essere forniti insieme',
          variant: 'destructive'
        });
        return;
      }
    }

    setSaving(true);
    try {
      const body: { credentials: Record<string, string>; config?: AcubeConfig } = { credentials };
      if (configDialog.template.type === 'acube') {
        body.config = acubeConfig;
      }
      const response = await apiClient.post<{ success: boolean }>(`/integrations/${configDialog.template.type}/configure`, body);

      if (response.success) {
        if (configDialog.template.type === 'firecrawl') {
          toast({
            title: 'Firecrawl attivato',
            description: 'API key salvata e server MCP "Firecrawl Web & Search" creato. I 5 tool sono ora disponibili in Agent Bindings e Workflow Builder.',
            duration: 6000
          });
        } else {
          toast({
            title: 'Configurazione salvata',
            description: 'Le credenziali sono state salvate correttamente'
          });
        }
        setConfigDialog({ open: false, template: null });
        loadIntegrations();
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile salvare la configurazione',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (type: string) => {
    setTesting(true);
    const integrationName = integrations.find(i => i.type === type)?.name || type;
    try {
      const response = await apiClient.post<{ success: boolean; error?: string; details?: string }>(`/integrations/${type}/test`);
      if (response.success && response.data?.success) {
        toast({
          title: 'Connessione riuscita',
          description: `Le credenziali ${integrationName} sono valide e funzionanti`
        });
        loadIntegrations();
      } else {
        const errorMsg = response.data?.error || response.error || 'Credenziali non valide';
        const details = response.data?.details;
        toast({
          title: 'Test connessione fallito',
          description: details ? `${errorMsg}\n\n${details}` : errorMsg,
          variant: 'destructive',
          duration: 10000 // Show longer for error details
        });
      }
    } catch (error) {
      toast({
        title: 'Errore di connessione',
        description: getErrorMessage(error) || 'Impossibile testare la connessione. Verifica la tua connessione internet.',
        variant: 'destructive'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleToggleEnabled = async (type: string, enabled: boolean) => {
    try {
      const endpoint = enabled ? 'enable' : 'disable';
      const response = await apiClient.post<{ success: boolean }>(`/integrations/${type}/${endpoint}`);
      
      if (response.success) {
        toast({
          title: enabled ? 'Integrazione attivata' : 'Integrazione disattivata',
          description: enabled 
            ? 'Il server MCP è stato creato e i tool sono disponibili' 
            : 'L\'integrazione è stata disattivata'
        });
        loadIntegrations();
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile modificare lo stato',
        variant: 'destructive'
      });
    }
  };

  const handleDisconnect = async (type: string, name: string) => {
    if (!confirm(`Sei sicuro di voler disconnettere ${name}? Le credenziali e il server MCP associato verranno rimossi.`)) {
      return;
    }

    setDisconnecting(type);
    try {
      const response = await apiClient.delete<{ success: boolean }>(`/integrations/${type}`);
      if (response.success) {
        toast({
          title: 'Integrazione disconnessa',
          description: `${name} è stata disconnessa e le credenziali rimosse`
        });
        setConfigDialog({ open: false, template: null });
        loadIntegrations();
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile disconnettere l\'integrazione',
        variant: 'destructive'
      });
    } finally {
      setDisconnecting(null);
    }
  };

  const googleSummary = {
    total: googleAccounts.length,
    authorized: googleAccounts.filter((a) => a.authorized === true).length,
    production: googleAccounts.filter((a) => a.app_published === true).length,
    testing: googleAccounts.filter((a) => a.app_published !== true).length
  };

  const getStatusBadge = (integration: IntegrationTemplate) => {
    if (integration.type === 'google' && googleSummary.authorized > 0) {
      return <Badge className="bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">Attivo</Badge>;
    }
    if (integration.status === 'active' && integration.health_status === 'healthy') {
      return <Badge className="bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">Attivo</Badge>;
    }
    if (integration.status === 'active' && integration.health_status === 'unhealthy') {
      return <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300">Errore connessione</Badge>;
    }
    if (integration.status === 'configured') {
      return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">Configurato</Badge>;
    }
    if (integration.status === 'error') {
      return <Badge className="bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300">Errore</Badge>;
    }
    return <Badge variant="outline">Da configurare</Badge>;
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'twilio':
        return <Phone className="h-8 w-8 text-red-500" />;
      case 'google':
        return <Chrome className="h-8 w-8 text-blue-500" />;
      case 'roc':
        return <Shield className="h-8 w-8 text-blue-700" />;
      case 'firecrawl':
        return <Flame className="h-8 w-8 text-orange-500" />;
      case 'pexels':
        return <ImageIcon className="h-8 w-8 text-teal-500" />;
      case 'apollo':
        return <Target className="h-8 w-8 text-indigo-600" />;
      case 'telegram':
        return <Send className="h-8 w-8 text-sky-500" />;
      default:
        return <Server className="h-8 w-8 text-gray-500" />;
    }
  };

  const getCredentialLabel = (field: string) => {
    const labels: Record<string, string> = {
      account_sid: 'Account SID',
      auth_token: 'Auth Token',
      api_key_sid: 'API Key SID',
      api_secret: 'API Secret',
      client_id: 'Client ID',
      client_secret: 'Client Secret',
      api_username: 'Username API',
      api_password: 'Password API',
      api_key: 'API Key'
    };
    return labels[field] || field;
  };

  const getCredentialPlaceholder = (field: string) => {
    const placeholders: Record<string, string> = {
      account_sid: 'AC...',
      auth_token: 'Il tuo Auth Token (dalla console Twilio)',
      api_key_sid: 'SK...',
      api_secret: 'Il tuo API Secret',
      client_id: 'xxxxx.apps.googleusercontent.com',
      client_secret: 'Il tuo Client Secret',
      api_username: 'Il tuo username ROC',
      api_password: 'La tua password ROC',
      api_key: 'fc-xxxxxxxxxxxxxxxxxxxxxxxx'
    };
    return placeholders[field] || '';
  };

  const getCredentialHelp = (field: string) => {
    const help: Record<string, string> = {
      account_sid: 'Sempre richiesto. Lo trovi nella dashboard Twilio.',
      auth_token: 'Opzionale. Necessario per API REST (templates WhatsApp).',
      api_key_sid: 'Opzionale. Consigliato per MCP e maggiore sicurezza.',
      api_secret: 'Opzionale. Da usare insieme ad API Key SID.',
      client_id: 'Dalla Google Cloud Console > API & Services > Credentials',
      client_secret: 'Dalla Google Cloud Console > API & Services > Credentials'
    };
    return help[field] || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Integrazioni Template</h3>
          <p className="text-sm text-muted-foreground">
            Configura integrazioni pre-costruite che aggiungono automaticamente server MCP e tool ai tuoi AI agent
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadIntegrations}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Connessione Meta Business (separata dal preset MCP meta-ads).
            Card allineata alle altre integrazioni, dentro la griglia. */}
        <MetaBusinessCard />

        {integrations.map((integration) => (
          <Card 
            key={integration.type} 
            className="relative flex h-full flex-col transition-all hover:shadow-md"
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-muted rounded-lg">
                    {getIcon(integration.type)}
                  </div>
                  <div>
                    <CardTitle className="text-base">{integration.name}</CardTitle>
                    <div className="flex flex-wrap items-center gap-1 mt-0.5">
                      {getStatusBadge(integration)}
                      {integration.type === 'google' && googleStatusError && (
                        <Badge className="gap-1 px-1.5 py-0 text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                          <AlertTriangle className="h-3 w-3" />
                          Stato non disponibile
                        </Badge>
                      )}
                      {integration.type === 'google' && !googleStatusError && googleSummary.total > 0 && (
                        <>
                          <Badge variant="outline" className="gap-1 px-1.5 py-0 text-[10px]">
                            <Users className="h-3 w-3" />
                            {googleSummary.total} account
                          </Badge>
                          {googleSummary.authorized > 0 && (
                            <Badge className="gap-1 px-1.5 py-0 text-[10px] bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">
                              <CheckCircle className="h-3 w-3" />
                              {googleSummary.authorized} autorizzati
                            </Badge>
                          )}
                          {googleSummary.production > 0 && (
                            <Badge className="px-1.5 py-0 text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                              {googleSummary.production} in produzione
                            </Badge>
                          )}
                          {googleSummary.testing > 0 && (
                            <Badge className="px-1.5 py-0 text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                              {googleSummary.testing} in testing
                            </Badge>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {integration.configured && (
                  <Switch
                    checked={integration.enabled}
                    onCheckedChange={(checked) => handleToggleEnabled(integration.type, checked)}
                  />
                )}
              </div>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col pt-0">
              <p className="text-sm text-muted-foreground mb-4">
                {integration.description}
              </p>

              {integration.type === 'firecrawl' && (
                <div className="flex items-center gap-1.5 text-xs bg-orange-50 border border-orange-200 text-orange-700 dark:bg-orange-950/40 dark:border-orange-900 dark:text-orange-300 rounded-md px-2 py-1.5 mb-3">
                  <Globe2 className="h-3 w-3 flex-shrink-0" />
                  <span>I dati delle URL passano per i server Firecrawl</span>
                </div>
              )}

              {integration.mcp_server_id && (
                <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 mb-3">
                  <Server className="h-3 w-3" />
                  <span>Server MCP attivo</span>
                </div>
              )}

              <div className="mt-auto flex gap-2">
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={() => openConfigDialog(integration)}
                >
                  <Settings className="h-4 w-4 mr-1" />
                  {integration.configured ? 'Modifica' : 'Configura'}
                </Button>

                {integration.configured && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestConnection(integration.type)}
                      disabled={testing}
                    >
                      {testing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : integration.health_status === 'healthy' ||
                        (integration.type === 'google' && googleSummary.authorized > 0) ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : integration.health_status === 'unhealthy' ? (
                        <XCircle className="h-4 w-4 text-red-500" />
                      ) : (
                        <Zap className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDisconnect(integration.type, integration.name)}
                      disabled={disconnecting === integration.type}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/40"
                    >
                      {disconnecting === integration.type ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Unlink className="h-4 w-4" />
                      )}
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {integrations.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            <Server className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Nessuna integrazione disponibile</p>
          </div>
        )}
      </div>

      <Card className="bg-blue-50/50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-100">Come funziona</h4>
              <p className="text-sm text-blue-700 dark:text-blue-200 mt-1">
                1. Clicca su <strong>Configura</strong> per inserire le tue credenziali<br />
                2. Attiva l'integrazione con lo switch<br />
                3. Un server MCP verrà creato automaticamente con i tool disponibili<br />
                4. I tuoi AI agent potranno usare questi tool per inviare SMS, WhatsApp, ecc.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={configDialog.open} onOpenChange={(open) => !open && setConfigDialog({ open: false, template: null })}>
        <DialogContent 
          className={`${configDialog.template?.type === 'acube' ? 'sm:!max-w-2xl' : configDialog.template?.type === 'google' ? 'sm:!max-w-5xl' : configDialog.template?.type === 'telegram' ? 'sm:!max-w-lg' : 'sm:!max-w-md'} ${configDialog.template?.type === 'acube' ? 'sm:!h-[85vh] sm:!max-h-[85vh]' : 'sm:!max-h-[85vh]'} sm:!grid-cols-1 sm:!flex sm:!flex-col`}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {configDialog.template && getIcon(configDialog.template.type)}
              Configura {configDialog.template?.name}
            </DialogTitle>
            <DialogDescription>
              Inserisci le credenziali per attivare questa integrazione
            </DialogDescription>
          </DialogHeader>

          {configDialog.template && (
            <div 
              className="space-y-4 py-4 overflow-y-auto overflow-x-hidden flex-1 min-h-0 pr-2"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#d1d5db #f3f4f6'
              }}
            >
              {configDialog.template.configured && configDialog.template.type !== 'google' && (
                <div className="bg-green-50 border border-green-200 dark:bg-green-950/40 dark:border-green-900 rounded-lg p-3 mb-2">
                  <p className="text-sm text-green-800 dark:text-green-300 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Credenziali gia configurate. Lascia vuoto per mantenere quelle esistenti.
                  </p>
                </div>
              )}
              {configDialog.template.type !== 'acube' && configDialog.template.type !== 'google' && (configDialog.template.required_credentials || []).map((field) => (
                <div key={field} className="space-y-2">
                  <Label className="flex items-center gap-1">
                    {getCredentialLabel(field)}
                    {!configDialog.template?.configured && <span className="text-red-500">*</span>}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showSecrets[field] ? 'text' : 'password'}
                      placeholder={configDialog.template?.configured ? '••••••••••••' : getCredentialPlaceholder(field)}
                      value={credentials[field] || ''}
                      onChange={(e) => setCredentials(prev => ({ ...prev, [field]: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }))}
                    >
                      {showSecrets[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              ))}

              {(configDialog.template.optional_credentials || []).length > 0 && configDialog.template.type !== 'roc' && configDialog.template.type !== 'acube' && (
                <>
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium text-foreground mb-1">Metodo di autenticazione</p>
                    <p className="text-xs text-muted-foreground mb-3">
                      Scegli almeno uno: Auth Token (per API REST) oppure API Key + Secret (per MCP)
                    </p>
                  </div>
                  {(configDialog.template.optional_credentials || []).map((field) => (
                    <div key={field} className="space-y-1">
                      <Label className="text-sm">{getCredentialLabel(field)}</Label>
                      <div className="relative">
                        <Input
                          type={showSecrets[field] ? 'text' : 'password'}
                          placeholder={configDialog.template?.configured ? '••••••••••••' : getCredentialPlaceholder(field)}
                          value={credentials[field] || ''}
                          onChange={(e) => setCredentials(prev => ({ ...prev, [field]: e.target.value }))}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }))}
                        >
                          {showSecrets[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">{getCredentialHelp(field)}</p>
                    </div>
                  ))}
                </>
              )}

              {configDialog.template.type === 'telegram' && (
                <div className="space-y-5">
                  <div className="bg-blue-50 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-900 rounded-lg p-3 text-sm space-y-2">
                    <p className="font-medium text-blue-900 dark:text-blue-200 flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      Bot Telegram (multi-account)
                    </p>
                    <p className="text-blue-800 dark:text-blue-300 text-xs">
                      Crea un bot con <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="underline font-medium">@BotFather</a> su Telegram, copia il token e incollalo qui sotto. Puoi registrare più bot per separare notifiche e conversazioni clienti.
                    </p>
                  </div>

                  <div className="space-y-3 border rounded-lg p-4">
                    <p className="text-sm font-medium">Aggiungi un nuovo bot</p>
                    <div className="space-y-2">
                      <Label htmlFor="tg-bot-token" className="text-xs">Token bot <span className="text-red-500">*</span></Label>
                      <Input
                        id="tg-bot-token"
                        type="password"
                        autoComplete="off"
                        placeholder="123456789:AAH..."
                        value={telegramForm.botToken}
                        onChange={(e) => setTelegramForm(s => ({ ...s, botToken: e.target.value }))}
                      />
                      <p className="text-[11px] text-muted-foreground">Formato <code>NUMERO:STRINGA</code> rilasciato da @BotFather.</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tg-bot-name" className="text-xs">Nome amichevole (opzionale)</Label>
                      <Input
                        id="tg-bot-name"
                        placeholder="es: Notifiche Helpdesk"
                        value={telegramForm.friendlyName}
                        onChange={(e) => setTelegramForm(s => ({ ...s, friendlyName: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Uso del bot</Label>
                      <div className="flex gap-2 flex-wrap">
                        {(['both', 'notifications', 'messaging'] as const).map(p => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setTelegramForm(s => ({ ...s, purpose: p }))}
                            className={`px-3 py-1.5 rounded-md text-xs border transition ${
                              telegramForm.purpose === p
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background hover:bg-muted border-border'
                            }`}
                          >
                            {p === 'both' ? 'Notifiche + Messaggi' : p === 'notifications' ? 'Solo notifiche' : 'Solo messaggi'}
                          </button>
                        ))}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        <strong>Notifiche</strong>: alert sistema verso utenti collegati. <strong>Messaggi</strong>: conversazioni bidirezionali con clienti (sender CRM).
                      </p>
                    </div>
                    <Button
                      onClick={handleAddTelegramBot}
                      disabled={telegramAdding || !telegramForm.botToken.trim()}
                      className="w-full"
                    >
                      {telegramAdding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                      Registra bot
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Bot registrati ({telegramBots.length})</p>
                      <Button variant="ghost" size="sm" onClick={loadTelegramBots} disabled={telegramLoading}>
                        <RefreshCw className={`h-3.5 w-3.5 ${telegramLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                    {telegramLoading && telegramBots.length === 0 ? (
                      <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Caricamento…
                      </div>
                    ) : telegramBots.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground text-sm border border-dashed rounded-lg">
                        Nessun bot registrato. Aggiungine uno qui sopra.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {telegramBots.map(bot => (
                          <div key={bot.id} className="border rounded-lg p-3 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-sm truncate">
                                  {bot.friendly_name || bot.display_name}
                                  {bot.username && (
                                    <span className="ml-2 text-xs text-muted-foreground">@{bot.username}</span>
                                  )}
                                </p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <Badge variant={bot.status === 'active' ? 'default' : 'destructive'} className="text-[10px]">
                                    {bot.status}
                                  </Badge>
                                  <Badge variant="outline" className="text-[10px]">
                                    {bot.purpose === 'both' ? 'notifiche + messaggi' : bot.purpose === 'notifications' ? 'notifiche' : 'messaggi'}
                                  </Badge>
                                </div>
                                {bot.last_error && (
                                  <p className="text-[11px] text-red-600 dark:text-red-400 mt-1 flex items-start gap-1">
                                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                    {bot.last_error}
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleTestTelegramBot(bot)}
                                  disabled={telegramTesting === bot.id}
                                  title="Test getMe"
                                >
                                  {telegramTesting === bot.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteTelegramBot(bot)}
                                  disabled={telegramDeleting === bot.id}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                                  title="Rimuovi bot"
                                >
                                  {telegramDeleting === bot.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {configDialog.template.type === 'acube' && (
                <div className="space-y-5">
                  {/* Informational banner + portal link */}
                  <div className="bg-blue-50 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-900 rounded-lg p-3 text-sm space-y-2">
                    <p className="font-medium text-blue-900 dark:text-blue-200 flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Integrazione fiscale A-Cube
                    </p>
                    <p className="text-blue-800 dark:text-blue-300 text-xs">
                      Configura A-Cube per fattura elettronica, scontrini telematici, conservazione decennale e gestione fatture fornitori. Le credenziali e i dati fiscali sensibili (codici fiscali, password AdE, PIN) vengono cifrati a riposo.
                    </p>
                    <a
                      href="https://app.acubeapi.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 dark:text-blue-300 underline hover:text-blue-900 dark:hover:text-blue-100"
                    >
                      Apri portale A-Cube ↗
                    </a>
                  </div>

                  {/* Environment toggle (sandbox/prod). The whole A-Cube
                      configuration form is wrapped in a TooltipProvider so the
                      inline <Hint> components work everywhere below. */}
                  <TooltipProvider delayDuration={150}>
                  <div className="border rounded-lg p-3 bg-muted/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold">Ambiente A-Cube</p>
                        <p className="text-xs text-muted-foreground">
                          {acubePolicy?.is_development
                            ? 'Dev/staging: puoi scegliere sandbox o produzione liberamente.'
                            : (acubePolicy?.sandbox_allowed
                                ? 'Tenant demo: sandbox abilitato in produzione.'
                                : 'Produzione: sandbox riservato al tenant demo.')}
                        </p>
                      </div>
                      {(!acubePolicy?.is_development && !acubePolicy?.sandbox_allowed) ? (
                        <div className="flex items-center gap-2">
                          <Input
                            readOnly
                            className="bg-muted/40 max-w-[180px] text-center font-medium"
                            value="Produzione (bloccato)"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={acubeConfig.environment === 'sandbox' ? 'default' : 'outline'}
                            disabled={!acubePolicy?.sandbox_allowed}
                            onClick={() => setAcubeConfig((c) => ({ ...c, environment: 'sandbox' }))}
                          >Sandbox</Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={acubeConfig.environment === 'production' ? 'default' : 'outline'}
                            onClick={() => setAcubeConfig((c) => ({ ...c, environment: 'production' }))}
                          >Produzione</Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sandbox banner: explains what's required vs production */}
                  {isAcubeSandbox ? (
                    <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-200 text-sm flex items-start gap-2">
                      <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        <p className="font-medium">Modalità sandbox A-Cube</p>
                        <p className="text-xs leading-relaxed">
                          Stai usando l'ambiente di test: a livello account bastano <strong>email + password
                          A-Cube</strong>. La registrazione fiscale (Business Registry, merchant E-Receipts)
                          si fa per singola <strong>ragione sociale</strong>. I documenti emessi non hanno
                          valenza fiscale.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="border rounded-lg p-3 bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-200 text-sm flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        <p className="font-medium">Modalità produzione — flusso guidato</p>
                        <ol className="text-xs leading-relaxed list-decimal pl-4 space-y-0.5">
                          <li>Compila le <strong>credenziali A-Cube</strong> dell'account (sez. 1).</li>
                          <li>Attiva i <strong>servizi</strong> che ti servono (sez. 2) — passa il mouse sul ? di ogni servizio per i prerequisiti.</li>
                          <li>Se attivi la <strong>conservazione</strong>, compila i dati del <strong>legale rappresentante</strong> (sez. 3).</li>
                          <li>Salva: il backend genera il <strong>token webhook</strong> del tenant e registra ragioni sociali e webhook per evento su A-Cube.</li>
                          <li>Esegui <strong>Verifica credenziali</strong> (sez. 5) e controlla che ogni servizio risulti <em>attivo</em>.</li>
                          <li>
                            Configura l'<strong>identità fiscale</strong> di ogni <strong>ragione sociale</strong>
                            (Aziende → Ragioni sociali): tipo soggetto, registrazione Business Registry e
                            attivazione E-Receipts (merchant).
                          </li>
                        </ol>
                      </div>
                    </div>
                  )}

                  {/* L'identità fiscale per-RS vive nel modale della singola
                      ragione sociale. Qui resta SOLO l'account. */}
                  <div className="border rounded-lg p-3 bg-muted/30 text-xs text-muted-foreground flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <p>
                      Questo è l'<strong>account A-Cube</strong> condiviso dal tenant. L'identità fiscale
                      di ogni <strong>ragione sociale</strong> (tipo soggetto, registrazione Business Registry,
                      merchant E-Receipts) si configura in <strong>Aziende → Ragioni sociali</strong>,
                      aprendo la singola RS.
                    </p>
                  </div>

                  {/* Group: A-Cube platform creds */}
                  <details open className="border rounded-lg p-3">
                    <summary className="cursor-pointer text-sm font-semibold">1. Credenziali piattaforma A-Cube</summary>
                    <div className="space-y-3 mt-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Email A-Cube *</Label>
                        <Input
                          type="email"
                          placeholder="account@example.com"
                          value={credentials.email || ''}
                          onChange={(e) => setCredentials(p => ({ ...p, email: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Password A-Cube *</Label>
                        <Input
                          type={showSecrets.password ? 'text' : 'password'}
                          placeholder={configDialog.template?.configured ? '••••••••••••' : 'Password'}
                          value={credentials.password || ''}
                          onChange={(e) => setCredentials(p => ({ ...p, password: e.target.value }))}
                        />
                      </div>
                    </div>
                  </details>

                  {/* Group: services — each toggle has a tooltip explaining
                      what it does and which production-only sections it
                      activates (legal-rep etc.). */}
                  <details open className="border rounded-lg p-3">
                    <summary className="cursor-pointer text-sm font-semibold flex items-center gap-2">
                      <span>2. Servizi attivati</span>
                      <Hint text={(
                        <>
                          Toggle indipendenti: attiva solo i servizi che usi davvero. Più servizi
                          attivi possono richiedere campi aggiuntivi (es. la conservazione richiede
                          i dati del legale rappresentante).
                        </>
                      )} />
                    </summary>
                    <div className="space-y-2 mt-3">
                      {([
                        {
                          key: 'service_einvoice',
                          label: 'Fattura elettronica clienti',
                          help: (
                            <>
                              Emissione e invio FE al SDI tramite A-Cube. <br />
                              <strong>Produzione richiede:</strong> credenziali A-Cube + ragione sociale
                              registrata nel Business Registry.
                            </>
                          ),
                        },
                        {
                          key: 'service_ereceipts',
                          label: 'Scontrini digitali E-Receipts IT',
                          help: (
                            <>
                              Emissione documenti commerciali tramite registratore telematico software
                              (merchant per ragione sociale, PEM per sede, cassa per postazione). <br />
                              <strong>Produzione richiede:</strong> credenziali A-Cube + merchant attivato
                              sulla ragione sociale + PEM attivata sulla sede.
                            </>
                          ),
                        },
                        {
                          key: 'service_legal_storage',
                          label: 'Conservazione decennale a norma',
                          help: (
                            <>
                              Conservazione sostitutiva 10 anni di tutte le fatture/scontrini secondo CAD/AgID. <br />
                              <strong>Produzione richiede:</strong> consenso esplicito + nome, cognome,
                              codice fiscale e ruolo del <strong>legale rappresentante</strong> per la
                              firma del contratto di servizio.
                            </>
                          ),
                        },
                        {
                          key: 'service_supplier_invoices',
                          label: 'Fatture passive (fornitori)',
                          help: (
                            <>
                              Ricezione delle fatture inviate dai tuoi fornitori via SDI con notifica webhook
                              <code> customer-invoice</code>. <br />
                              <strong>Produzione richiede:</strong> credenziali A-Cube + ragione sociale
                              registrata nel Business Registry con fatture passive abilitate.
                            </>
                          ),
                        },
                      ] as const).map(({ key, label, help }) => {
                        const checked = !!acubeConfig[key];
                        const showSandboxLegalNote = isAcubeSandbox && checked && key === 'service_legal_storage';
                        return (
                          <div key={key} className="space-y-0.5">
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => setAcubeConfig((c) => ({ ...c, [key]: e.target.checked }))}
                              />
                              <span className="flex items-center gap-1.5">
                                {label}
                                <Hint text={help} />
                              </span>
                            </label>
                            {showSandboxLegalNote && (
                              <p className="text-xs text-muted-foreground pl-6">
                                In sandbox i dati del legale rappresentante non sono richiesti — saranno obbligatori passando in produzione.
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </details>

                  {/* Group: conservazione (solo se service_legal_storage) — production only */}
                  {!isAcubeSandbox && acubeConfig.service_legal_storage && (
                    <details open className="border rounded-lg p-3">
                      <summary className="cursor-pointer text-sm font-semibold">3. Conservazione e firma legale</summary>
                      <div className="space-y-3 mt-3">
                        <label className="flex items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={!!acubeConfig.legal_storage_consent}
                            onChange={(e) => setAcubeConfig((c) => ({
                              ...c,
                              legal_storage_consent: e.target.checked,
                              legal_storage_consent_at: e.target.checked ? new Date().toISOString() : undefined,
                            }))}
                          />
                          <span>Acconsento alla conservazione decennale a norma per i miei documenti fiscali (obbligatorio).</span>
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            placeholder="Nome legale rappresentante *"
                            value={acubeConfig.legal_representative_first_name || ''}
                            onChange={(e) => setAcubeConfig((c) => ({ ...c, legal_representative_first_name: e.target.value }))}
                          />
                          <Input
                            placeholder="Cognome *"
                            value={acubeConfig.legal_representative_last_name || ''}
                            onChange={(e) => setAcubeConfig((c) => ({ ...c, legal_representative_last_name: e.target.value }))}
                          />
                        </div>
                        <Input
                          placeholder={configDialog.template?.configured ? 'Codice fiscale legale rappresentante (lascia vuoto per mantenere)' : 'Codice fiscale legale rappresentante *'}
                          value={credentials.legal_representative_fiscal_code || ''}
                          onChange={(e) => setCredentials(p => ({ ...p, legal_representative_fiscal_code: e.target.value.toUpperCase() }))}
                        />
                        <select
                          className="w-full border rounded px-3 py-2 text-sm bg-background"
                          value={acubeConfig.legal_representative_role || ''}
                          onChange={(e) => setAcubeConfig((c) => ({ ...c, legal_representative_role: e.target.value }))}
                        >
                          <option value="">Seleziona ruolo legale rappresentante *</option>
                          <option value="Amministratore Unico">Amministratore Unico</option>
                          <option value="Presidente CdA">Presidente del Consiglio di Amministrazione</option>
                          <option value="Amministratore Delegato">Amministratore Delegato</option>
                          <option value="Consigliere Delegato">Consigliere Delegato</option>
                          <option value="Procuratore Speciale">Procuratore Speciale</option>
                          <option value="Liquidatore">Liquidatore</option>
                          <option value="Titolare">Titolare (ditta individuale)</option>
                        </select>
                      </div>
                    </details>
                  )}

                  {/* Group: webhook */}
                  <details open className="border rounded-lg p-3">
                    <summary className="cursor-pointer text-sm font-semibold">4. Webhook A-Cube</summary>
                    <div className="space-y-3 mt-3">
                      <div className="space-y-1">
                        <Label className="text-sm">URL base dei webhook</Label>
                        <Input
                          readOnly
                          className="bg-muted/40"
                          value={acubeWebhook
                            ? `${acubeWebhook.base_url}/<evento>`
                            : 'disponibile dopo il salvataggio'}
                        />
                        <p className="text-xs text-muted-foreground">
                          A-Cube chiama una URL per evento, autenticata con <code>Authorization: Bearer</code> (token generato lato server:{' '}
                          {acubeWebhook?.has_token
                            ? <span className="text-green-600 dark:text-green-400 font-medium">presente</span>
                            : <span>verrà generato al salvataggio</span>}) e firmata RFC 9421 (Ed25519).
                        </p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Chiave pubblica di firma ({acubeWebhook?.public_key.environment ?? acubePolicy?.active_environment ?? '—'})</Label>
                        {acubeWebhook ? (
                          <div className="text-xs space-y-0.5">
                            <AcubePublicKeyLine label="Corrente" entry={acubeWebhook.public_key.current} />
                            <AcubePublicKeyLine label="Prossima" entry={acubeWebhook.public_key.next} />
                            {acubeWebhook.public_key.error && (
                              <p className="text-destructive">Ultimo recupero fallito: {acubeWebhook.public_key.error}</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">Recuperata automaticamente da A-Cube alla prima richiesta.</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Eventi registrati per ragione sociale</Label>
                        {acubeCompanies.length === 0 ? (
                          <p className="text-xs text-muted-foreground">Nessuna ragione sociale con provider fiscale A-Cube.</p>
                        ) : (
                          <div className="space-y-1 text-xs">
                            {acubeCompanies.map((c) => (
                              <div key={c.company_id} className="border rounded px-2 py-1 space-y-1">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="truncate font-medium">{c.legal_name || c.fiscal_id || '—'}</span>
                                  <span className="shrink-0 text-muted-foreground">
                                    {c.webhook_synced_at
                                      ? `${c.webhook_events_registered.length} eventi · ${new Date(c.webhook_synced_at).toLocaleString('it-IT')}`
                                      : 'webhook non registrati'}
                                  </span>
                                </div>
                                {c.webhook_events_registered.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {c.webhook_events_registered.map((ev) => (
                                      <Badge key={ev} variant="outline" className="font-mono text-[10px]">{ev}</Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Gli eventi derivano dai servizi attivi (sez. 2) e vengono riallineati a ogni salvataggio o sincronizzazione.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button type="button" size="sm" variant="outline" disabled={acubeBusy || !configDialog.template?.configured}
                          onClick={() => void handleAcubeSyncBusinessRegistry()}>
                          Sincronizza ragioni sociali e webhook con A-Cube
                        </Button>
                      </div>
                    </div>
                  </details>

                  {/* Group: diagnostica */}
                  <details className="border rounded-lg p-3" open>
                    <summary className="cursor-pointer text-sm font-semibold">5. Test connessione e diagnostica</summary>
                    <div className="space-y-3 mt-3">
                      <p className="text-xs text-muted-foreground">
                        Effettua un login reale su <code>common.api.acubeapi.com</code> con l'ambiente attivo e mostra ruoli e scadenza del token.
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" size="sm" variant="outline"
                          disabled={acubeBusy || !configDialog.template?.configured}
                          onClick={() => handleAcubeVerifyCredentials()}>
                          {acubeBusy ? 'Verifica in corso…' : 'Verifica credenziali'}
                        </Button>
                        {acubeCheck && (
                          <span className={`text-xs ${acubeCheck.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {acubeCheck.ok ? '✅' : '❌'} [{acubeCheck.environment}] {acubeCheck.message}
                            {acubeCheck.token_expires_at && <> — scade {new Date(acubeCheck.token_expires_at).toLocaleString('it-IT')}</>}
                            {acubeCheck.roles.length > 0 && <> — ruoli: {acubeCheck.roles.join(', ')}</>}
                          </span>
                        )}
                      </div>

                      {/* Per-service status badges — fed by backend service_status
                          (configured/not_configured/error) which is written after every
                          successful BusinessRegistry onboarding to A-Cube. */}
                      <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t">
                        {([
                          { key: 'service_einvoice', short: 'einvoice', label: 'Fattura elettronica' },
                          { key: 'service_ereceipts', short: 'ereceipts', label: 'Scontrini elettronici' },
                          { key: 'service_legal_storage', short: 'legal_storage', label: 'Conservazione 10 anni' },
                          { key: 'service_supplier_invoices', short: 'supplier_invoices', label: 'Fatture fornitori' },
                        ] as const).map(({ key, short, label }) => {
                          const enabled = !!acubeConfig[key];
                          const remote = acubeConfig.service_status?.[short];
                          const lastErr = acubeConfig.last_onboard_error || '';
                          let badge: { icon: string; cls: string; text: string; title?: string };
                          if (!enabled) {
                            badge = { icon: '○', cls: 'text-muted-foreground', text: 'disattivo' };
                          } else if (remote === 'error') {
                            badge = { icon: '❌', cls: 'text-red-600 dark:text-red-400', text: 'errore A-Cube', title: lastErr || 'Onboarding fallito' };
                          } else if (remote === 'not_configured') {
                            badge = { icon: '⏳', cls: 'text-amber-600 dark:text-amber-400', text: 'non sincronizzato', title: 'Nessuna ragione sociale ha questo servizio attivo su A-Cube: usa "Sincronizza ragioni sociali con A-Cube"' };
                          } else if (remote === 'configured') {
                            badge = { icon: '✅', cls: 'text-green-600 dark:text-green-400', text: 'attivo', title: 'Confermato da A-Cube' };
                          } else {
                            badge = { icon: '·', cls: 'text-muted-foreground', text: 'sconosciuto', title: 'Esegui sincronizzazione per popolare lo stato' };
                          }
                          return (
                            <div key={key} className="flex items-center justify-between border rounded px-2 py-1" title={badge.title}>
                              <span>{label}</span>
                              <span className={`font-medium ${badge.cls}`}>{badge.icon} {badge.text}</span>
                            </div>
                          );
                        })}
                      </div>

                      <div className="pt-2 border-t space-y-1">
                        <Label className="text-sm">Business registry per ragione sociale</Label>
                        {acubeCompanies.length === 0 ? (
                          <p className="text-xs text-muted-foreground">Nessuna ragione sociale con provider fiscale A-Cube.</p>
                        ) : (
                          <div className="space-y-1 text-xs">
                            {acubeCompanies.map((c) => (
                              <div key={c.company_id} className="flex items-center justify-between border rounded px-2 py-1 gap-2">
                                <span className="truncate">
                                  <span className="font-medium">{c.legal_name || '—'}</span>
                                  {c.fiscal_id && <code className="ml-1 text-muted-foreground">{c.fiscal_id}</code>}
                                </span>
                                <span className="shrink-0 text-muted-foreground">
                                  {c.brc_synced_at
                                    ? <>✅ {new Date(c.brc_synced_at).toLocaleDateString('it-IT')}
                                        {' · '}{[c.customer_invoice_enabled && 'FE', c.supplier_invoice_enabled && 'passivo', c.receipts_enabled && 'scontrini', c.apply_legal_storage && 'conservazione'].filter(Boolean).join(', ') || 'nessun servizio'}</>
                                    : '⏳ non registrata'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="text-xs space-y-1 pt-2 border-t">
                        {acubePolicy?.active_environment && (
                          <p>Ambiente attivo: <strong>{acubePolicy.active_environment}</strong>
                            {acubePolicy?.tenant_slug && <> &middot; Tenant: <code>{acubePolicy.tenant_slug}</code></>}
                          </p>
                        )}
                        <p className="text-muted-foreground">
                          Delega e identità fiscale sono gestite per singola ragione sociale
                          (Aziende → Ragioni sociali).
                        </p>
                      </div>
                    </div>
                  </details>
                  </TooltipProvider>
                </div>
              )}

              {configDialog.template.type === 'roc' && (
                <>
                  <div className="border-t pt-4 mt-2 space-y-3">
                    <div className="bg-blue-50 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-900 rounded-lg p-3 text-sm">
                      <p className="font-medium text-blue-900 dark:text-blue-200 mb-1 flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Obbligatorio per legge
                      </p>
                      <p className="text-blue-800 dark:text-blue-300 text-xs">
                        La verifica del Registro delle Opposizioni è obbligatoria per campagne outbound verso numeri italiani (D.Lgs. 196/2003 — Codice Privacy). Registrati come operatore su{' '}
                        <a href="https://www.registrodelleopposizioni.it" target="_blank" rel="noopener noreferrer" className="underline font-medium">registrodelleopposizioni.it</a>{' '}
                        per ottenere le credenziali API.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">Endpoint URL <span className="text-xs text-muted-foreground">(opzionale)</span></Label>
                      <Input
                        type="text"
                        placeholder="https://api.registrodelleopposizioni.it"
                        value={credentials['endpoint_url'] || ''}
                        onChange={(e) => setCredentials(prev => ({ ...prev, endpoint_url: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground">Lascia vuoto per usare l'endpoint ufficiale ROC</p>
                    </div>
                  </div>
                </>
              )}

              {configDialog.template.type === 'firecrawl' && (
                <div className="space-y-3 mt-2">
                  <div className="bg-orange-50 border border-orange-200 dark:bg-orange-950/40 dark:border-orange-900 rounded-lg p-3 text-sm">
                    <p className="font-medium text-orange-900 dark:text-orange-200 mb-1 flex items-center gap-2">
                      <Globe2 className="h-4 w-4" />
                      Nota sulla privacy
                    </p>
                    <p className="text-orange-800 dark:text-orange-300 text-xs">
                      I contenuti delle URL aggiunte alla knowledge base vengono inviati ai server Firecrawl per l'estrazione. Non aggiungere URL con contenuti sensibili o riservati.
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-sm">
                    <p className="font-medium mb-1">Come ottenere la API Key:</p>
                    <p className="text-muted-foreground text-xs mb-2">
                      Registrati su Firecrawl e genera una chiave API. Il piano gratuito include 500 crediti/mese.
                    </p>
                    <a
                      href="https://firecrawl.dev/app/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-600 dark:text-orange-400 hover:underline text-xs inline-flex items-center gap-1 font-medium"
                    >
                      Ottieni API Key su firecrawl.dev <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              )}

              {configDialog.template.type === 'twilio' && (
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <p className="font-medium mb-2">Dove trovare le credenziali:</p>
                  <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                    <li>Accedi alla <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1">Twilio Console <ExternalLink className="h-3 w-3" /></a></li>
                    <li>Account SID: nella dashboard principale</li>
                    <li>API Key: Account → API keys & tokens → Create API key</li>
                  </ol>
                </div>
              )}

              {configDialog.template.type === 'fic' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-900 rounded-lg p-3 text-sm space-y-2">
                    <p className="font-medium text-blue-900 dark:text-blue-200 flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Integrazione Fatture in Cloud
                    </p>
                    <p className="text-blue-800 dark:text-blue-300 text-xs">
                      1) Salva Client ID e Client Secret dell'app OAuth creata su Fatture in Cloud.
                      2) Collega l'account con il pulsante OAuth. 3) Seleziona l'azienda e attiva il webhook
                      per gli aggiornamenti di stato SdI in tempo reale.
                    </p>
                    <a
                      href="https://developers.fattureincloud.it/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline text-xs inline-flex items-center gap-1 font-medium"
                    >
                      Console sviluppatori Fatture in Cloud <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>

                  {configDialog.template.configured && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Button type="button" size="sm" variant="outline" disabled={ficBusy} onClick={() => void handleFicConnect()}>
                          {ficBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5 mr-1" />}
                          {ficConnected ? 'Ricollega account (OAuth)' : 'Collega account (OAuth)'}
                        </Button>
                        {ficConnected === true && <Badge variant="default">Connesso</Badge>}
                        {ficConnected === false && <Badge variant="outline">Non connesso</Badge>}
                      </div>

                      {ficConnected && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium">Azienda Fatture in Cloud attiva</p>
                          <select
                            className="h-9 w-full border rounded-md px-2 text-sm bg-background"
                            value={ficSelectedCompany != null ? String(ficSelectedCompany) : ''}
                            disabled={ficBusy || ficCompanies.length === 0}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              if (Number.isFinite(n) && n > 0) void handleFicSelectCompany(n);
                            }}
                          >
                            <option value="">— seleziona azienda —</option>
                            {ficCompanies.map((c) => (
                              <option key={c.id} value={String(c.id)}>{c.name} (#{c.id})</option>
                            ))}
                          </select>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={ficBusy || ficSelectedCompany == null}
                            onClick={() => void handleFicSubscribeWebhook()}
                          >
                            {ficBusy && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                            Attiva webhook stato SdI
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {configDialog.template.type === 'google' && (
                <GoogleIntegrationConfig
                  configured={configDialog.template.configured}
                  templateName={configDialog.template.name}
                  onIntegrationChanged={loadIntegrations}
                  onRequestClose={() => setConfigDialog({ open: false, template: null })}
                />
              )}
            </div>
          )}

          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => setConfigDialog({ open: false, template: null })}>
              {configDialog.template?.type === 'telegram' ? 'Chiudi' : 'Annulla'}
            </Button>
            {configDialog.template?.type !== 'telegram' && configDialog.template?.type !== 'google' && (
              <Button onClick={handleSaveCredentials} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salva configurazione
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
